import { useContext } from "react";
import {
  Avatar,
  Box,
  Container,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Button from "../common/Button";
import Card from "../common/Card";
import { useTheme } from "@mui/material/styles";
import { ColorModeContext, tokens } from "../../theme";
import { useLocation, useParams } from "react-router-dom";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import {
  ChangeForgotPassword,
  resetPasswordDetails,
  ResetPasswordInfo,
  updateElementInAuthSlice,
  useAuthSlice,
  UserInfo,
} from "../../redux/slice/authSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import CustomPasswordTextField from "../common/CustomPasswordTextField";
import { useNavigation } from "../../hooks/useNavigation";
import { FAILED_CODE, SUCCESS_CODE } from "../../service/ServiceConstants";
import { setSingleAlertObj, SingleAlertInfo } from "../../redux/slice/commonSlice";
import { useBranding } from "../../BrandingContext";

const ResetPasswordOnLogin: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { navigate } = useNavigation();
  const colorMode = useContext(ColorModeContext);
  const dispatch = useAppDispatch();
  const { control, handleSubmit, watch } = useForm<ResetPasswordInfo>();
  const location = useLocation();
  const { userInfo } = useAuthSlice();
  const branding = useBranding();

  const userId: number = location.state?.userId;

  const handleBackToSignIn = () => {
    dispatch(resetPasswordDetails());
    navigate("/signIn");
  };

  const onSubmit = (data: ResetPasswordInfo) => {
    if (watch("newPassword") !== watch("confirmPassword")) {
      const alertObj: SingleAlertInfo = {
        message: "Passwords do not match",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
      return;
    }

    const restPassword: ResetPasswordInfo = {
      code: "",
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
      passwordResetRequired: false,
      userId: userId,
    };

    handlePasswordReset(restPassword);
  };

  const handlePasswordReset = async (data: ResetPasswordInfo) => {
    const response = await dispatch(ChangeForgotPassword(data));

    if (response?.payload?.code === SUCCESS_CODE) {

      const alertObj: SingleAlertInfo = {
        message: "Your password has been changed successfully.",
        alertType: "success",
      };

      dispatch(setSingleAlertObj(alertObj));
      dispatch(updateElementInAuthSlice({
        elementName: "userInfo",
        value: {
          ...userInfo,
          passwordResetRequired: false,
        } as UserInfo,
      }));
      handleBackToSignIn();

    } else if (response?.payload?.code === FAILED_CODE) {

      const alertObj: SingleAlertInfo = {
        message: "Password reset failed. Please try again.",
        alertType: "error",
      };

      dispatch(setSingleAlertObj(alertObj));
    };
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          borderBottom: 1,
          borderColor:
            theme.palette.mode === "dark" ? "primary.dark" : "secondary.main",
        }}
      >
        <Grid
          container
          spacing={2}
          p={2}
          display="flex"
          style={{ backgroundColor: colors.grey[800] }}
        >
          <Grid item sm={4}></Grid>
          <Grid item sm={4} style={{ textAlign: "center" }}>
            <img
              alt="logo"
              src={theme.palette.mode === "dark" ? branding?.logoWhite : branding?.logo || require("../../branding-assets/v3biomed/logo.png")}
              style={{
                height: "80px",
                objectFit: "contain",
                maxWidth: "100%",
              }}
            />
          </Grid>
          <Grid item sm={4} style={{ textAlign: "end" }}>
            <IconButton onClick={colorMode.toggleColorMode}>
              {theme.palette.mode === "dark" ? (
                <LightModeOutlined />
              ) : (
                <DarkModeOutlined />
              )}
            </IconButton>
          </Grid>
        </Grid>
      </Box>
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: "100vh" }}
      >
        <Container component="main" maxWidth="sm">
          <Card>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar sx={{ m: 1 }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography variant="h3">
                <b>Reset Password</b>
              </Typography>
              <Box sx={{ textAlign: "center", mt: 2, color: colors.grey[500] }}>
                <Typography variant="h6">
                  Please enter your new password and confirm it.
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <CustomPasswordTextField
                    margin="normal"
                    fullWidth
                    label="New Password"
                    controllerName="newPassword"
                    control={control}
                    rules={
                      {
                        required: "Password is required.",
                        minLength: {
                          value: 12,
                          message: "Password should be at least 12 characters.",
                        },
                        pattern: {
                          value: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
                          message: "Password must contain at least one uppercase letter, one number, and one special character"
                        },
                      }
                    }
                  />
                  <CustomPasswordTextField
                    margin="normal"
                    fullWidth
                    label="Confirm Password"
                    controllerName="confirmPassword"
                    control={control}
                    rules={{
                      required: `Confirm Password is required.`,
                      validate: (value: string) =>
                        value === watch("newPassword") || "Passwords do not match",
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    style={{
                      borderRadius: 50,
                      padding: "15px",
                      marginTop: "20px",
                    }}
                  >
                    Reset Password
                  </Button>
                </form>
                <Grid container>
                  <Grid item xs sx={{ textAlign: "center", mt: 2 }}>
                    <Typography
                      variant="h6"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleBackToSignIn()}
                    >
                      Back to Sign In
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Card>
        </Container>
      </Grid>
    </>
  );
};

export default ResetPasswordOnLogin;
